import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ShieldCheck, ArrowRight, KeyRound } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";
import Logo from "../components/Logo";

export default function AdminLoginPage() {
  const { login, verify2FA } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ email: "admin@frontunirdc-france.org", password: "" });
  const [twoFA, setTwoFA] = useState(null); // { user_id, email } | null
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      const res = await login(form.email, form.password);
      if (res?.two_factor_required) {
        setTwoFA({ user_id: res.user_id, email: res.email });
        toast.success("Saisissez votre code à 6 chiffres.");
        return;
      }
      // Accès admin ouvert à : admin, responsable, protocole, securite, communication, media, moderator
      const STAFF = ["admin", "responsable", "protocole", "securite", "communication", "media", "moderator"];
      if (!STAFF.includes(res.role)) throw new Error("Cet espace est réservé au personnel autorisé.");
      toast.success("Connecté.");
      // Protocole / Sécurité → direct sur scan ; autres → admin
      if (["protocole", "securite"].includes(res.role)) nav("/admin/scan");
      else nav("/admin");
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Identifiants invalides");
    } finally { setLoading(false); }
  };

  const submit2FA = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      const u = await verify2FA(twoFA.user_id, code.trim());
      const STAFF = ["admin", "responsable", "protocole", "securite", "communication", "media", "moderator"];
      if (!STAFF.includes(u.role)) throw new Error("Cet espace est réservé au personnel autorisé.");
      toast.success("Connecté.");
      if (["protocole", "securite"].includes(u.role)) nav("/admin/scan");
      else nav("/admin");
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Code invalide");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-ink text-white grid place-items-center p-6 grain relative" data-testid="admin-login-page">
      <div className="absolute inset-0 opacity-10 bg-[radial-gradient(circle_at_30%_20%,#D4AF37,transparent_60%)]" />
      <div className="relative w-full max-w-md">
        <div className="text-center">
          <Logo className="w-20 h-20 mx-auto" />
          <div className="w-14 h-14 bg-gold text-ink rounded-sm grid place-items-center mx-auto mt-4">
            <ShieldCheck className="w-7 h-7" />
          </div>
          <h1 className="font-serif text-3xl mt-6 leading-tight">Administration</h1>
          <p className="mt-2 text-white/65 text-sm">Front Uni pour la RDC</p>
        </div>
        {twoFA ? (
          <form onSubmit={submit2FA} className="mt-10 bg-white text-foreground rounded-sm p-8 space-y-4 shadow-2xl" data-testid="2fa-form">
            <div className="text-center">
              <KeyRound className="w-10 h-10 mx-auto text-gold-dark" />
              <p className="mt-3 font-serif text-lg text-ink">Vérification 2FA</p>
              <p className="text-xs text-foreground/60 mt-1">Code de votre application d'authentification</p>
            </div>
            <input required autoFocus value={code} onChange={(e) => setCode(e.target.value)}
                   inputMode="numeric" pattern="[0-9]{6}" maxLength={6}
                   placeholder="000000"
                   data-testid="2fa-code-input"
                   className="w-full border border-border rounded-sm p-4 text-center text-2xl tracking-[0.5em] font-mono focus:border-ink outline-none" />
            <button disabled={loading} className="btn-primary w-full justify-center" data-testid="2fa-submit">
              {loading ? "…" : <>Valider <ArrowRight className="w-4 h-4" /></>}
            </button>
            <button type="button" onClick={() => { setTwoFA(null); setCode(""); }}
                    className="block w-full text-center text-xs text-foreground/55 hover:text-ink">
              ← Recommencer
            </button>
          </form>
        ) : (
          <form onSubmit={submit} className="mt-10 bg-white text-foreground rounded-sm p-8 space-y-4 shadow-2xl">
            <div>
              <label className="block text-[0.7rem] uppercase tracking-[0.16em] font-semibold text-foreground/60 mb-2">Email</label>
              <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                     className="w-full border border-border rounded-sm p-3 text-sm focus:border-ink outline-none" data-testid="admin-email" />
            </div>
            <div>
              <label className="block text-[0.7rem] uppercase tracking-[0.16em] font-semibold text-foreground/60 mb-2">Mot de passe</label>
              <input required type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
                     className="w-full border border-border rounded-sm p-3 text-sm focus:border-ink outline-none" data-testid="admin-password" />
            </div>
            <button disabled={loading} className="btn-primary w-full justify-center mt-2" data-testid="admin-login-submit">
              {loading ? "…" : <>Accéder à l'administration <ArrowRight className="w-4 h-4" /></>}
            </button>
            <Link to="/mot-de-passe-oublie" className="block text-center text-xs text-foreground/55 hover:text-ink mt-2"
                  data-testid="admin-forgot-link">
              Mot de passe oublié ?
            </Link>
          </form>
        )}
        <div className="mt-6 text-center text-sm text-white/55">
          <Link to="/" className="hover:text-gold">← Retour au site</Link>
        </div>
      </div>
    </div>
  );
}
